/**
 * Core authentication flow logic for new token-based auth
 *
 * Handles all 6 scenarios using only:
 * - Readable cookie (profileStatus) for token freshness
 * - Profile status from localStorage
 *
 * Tokens (AT/RT) are ONLY in httpOnly cookies - UFE never accesses them
 */

import refreshTokenAPI from 'services/auth/newFlow/api/refreshToken';
import cookieUtils from 'utils/Cookies';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import userUtils from 'utils/User';
import {
    AUTH_SCENARIOS, PROFILE_STATUS, PROFILE_STATUS_COOKIE_NAME, WWW_AUTHENTICATE, IS_SSI_COOKIE_NAME
} from 'services/auth/newFlow/constants';
import wwwAuthParser from 'services/auth/newFlow/utils/wwwAuthenticateParser';
import Empty from 'constants/empty';
import RCPSCookies from 'utils/RCPSCookies';

// Token refresh state
let newAuthFlowRefreshInProgress = false;
let refreshPromise = null;

// Auth operation state (login/generateToken in progress)
let authOperationInProgress = false;

// ============================================================================
// Cookie Utilities
// ============================================================================
export const hasValidProfileStatusCookie = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    const profileStatusCookie = cookieUtils.read(PROFILE_STATUS_COOKIE_NAME);

    if (!profileStatusCookie) {
        return false;
    }

    // Handle pipe-delimited format "4|1765481632"
    // parts[0] = profile status, parts[1] = access token expiry timestamp
    const parts = profileStatusCookie.split('|');

    const validValues = ['0', '2', '3', '4'];

    return parts[0] && validValues.includes(parts[0]);
};

export const hasValidSSICookie = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    const isSSICookie = cookieUtils.read(IS_SSI_COOKIE_NAME);

    return isSSICookie !== null && isSSICookie !== undefined;
};

/**
 * Check if the access token expires within the specified threshold
 * By default, uses environment-based threshold:
 * - Production: 30 minutes (half of 60 min TTL)
 * - Lower environments (QA): 2.5 minutes (half of 5 min TTL)
 * @param {number} thresholdMinutes - Number of minutes before expiration to trigger refresh (optional: defaults to env-based threshold)
 * @returns {boolean} True if token expires within threshold, false otherwise
 */
export const isTokenExpiringWithinThreshold = thresholdMinutes => {
    if (typeof window === 'undefined') {
        return false;
    }

    const profileStatusCookie = cookieUtils.read(PROFILE_STATUS_COOKIE_NAME);
    // Handle pipe-delimited format "4|1765481632"
    // parts[0] = profile status, parts[1] = access token expiry timestamp
    const parts = profileStatusCookie?.split('|') || Empty.Array;

    if (!parts[1]) {
        return false;
    }

    try {
        const expiryTimestamp = parseInt(parts[1], 10) * 1000; // Convert to milliseconds
        const currentTimestamp = Date.now();

        // If no threshold provided, use environment-based default
        let thresholdMs;

        if (thresholdMinutes === undefined) {
            // Production: 30 minutes (half of 60 min TTL)
            // Lower environments (QA): 2.5 minutes (half of 5 min TTL)
            const isProd = typeof Sephora !== 'undefined' && Sephora.UFE_ENV === 'PROD';
            const defaultThresholdMinutes = isProd ? 30 : 2.5;
            thresholdMs = defaultThresholdMinutes * 60 * 1000;
        } else {
            thresholdMs = thresholdMinutes * 60 * 1000; // Convert minutes to milliseconds
        }

        const timeUntilExpiry = expiryTimestamp - currentTimestamp;

        // Return true if:
        // 1. Token already expired (timeUntilExpiry <= 0)
        // 2. Token expires within threshold (0 < timeUntilExpiry <= thresholdMs)
        return timeUntilExpiry <= thresholdMs;
    } catch (error) {
        Sephora.logger.error('[New Auth Flow] Failed to parse token expiry cookie', { error, profileStatusCookie });

        return false;
    }
};

// ============================================================================
// Token Refresh
// ============================================================================

export const initRefreshToken = async (options = {}) => {
    const { causedBy = 'new_auth_flow' } = options;

    // Use global newAuth.headScript deduplication if available (prevents duplicate refresh chains)
    // This ensures all code paths (headScript + service layer) share the same deduplication logic
    if (typeof window !== 'undefined' && window.NewAuthFlow?.refreshTokens) {
        Sephora.logger.verbose('[New Auth Flow] Using headScript global refresh', { causedBy });

        return window.NewAuthFlow.refreshTokens({ causedBy });
    }

    // Fallback: Service layer deduplication (for backward compatibility or if headScript hasn't loaded yet)
    // Prevent duplicate refreshToken API calls (e.g., Scenario 3: multiple API responses with www-authenticate header)
    // All callers wait on the same promise, only 1 actual API call is made
    if (newAuthFlowRefreshInProgress && refreshPromise) {
        Sephora.logger.verbose('[New Auth Flow] Refresh in progress, reusing promise');

        return refreshPromise;
    }

    newAuthFlowRefreshInProgress = true;

    try {
        Sephora.logger.verbose('[New Auth Flow] Refreshing tokens', { causedBy });

        refreshPromise = refreshTokenAPI.refreshToken(
            {},
            {
                headers: { 'X-CAUSED-BY-URL': causedBy }
            }
        );

        const result = await refreshPromise;

        Sephora.logger.verbose('[New Auth Flow] Refresh complete', {
            causedBy,
            success: !result.errors,
            profileStatus: result.profileSecurityStatus
        });

        return result;
    } catch (error) {
        Sephora.logger.error('[New Auth Flow] Refresh failed', { causedBy, error });
        throw error;
    } finally {
        newAuthFlowRefreshInProgress = false;
        refreshPromise = null;
    }
};

export const isRefreshInProgress = () => {
    // Check newAuth headScript state first (if available)
    // This ensures consistency if both headScript and service layer are loaded at the same time
    if (typeof window !== 'undefined' && window.NewAuthFlow?.isRefreshInProgress) {
        return window.NewAuthFlow.isRefreshInProgress();
    }

    // Fallback to service layer state
    return newAuthFlowRefreshInProgress;
};

// ============================================================================
// Auth Operation State (Login/GenerateToken)
// ============================================================================

export const setAuthOperationInProgress = inProgress => {
    if (!RCPSCookies.isRCPSCookieBasedAuthEnabled()) {
        return;
    }

    authOperationInProgress = inProgress;
    Sephora.logger.verbose('[New Auth Flow] Auth operation state changed', { inProgress });
};

export const isAuthOperationInProgress = () => authOperationInProgress;

// ============================================================================
// TEMPORARY: Migration helpers for old localStorage tokens
// TODO: Remove these functions once all users migrate to new cookie-based flow
// ============================================================================

/**
 * TEMPORARY: Parse JWT token and get expiration time
 * This is only needed for migration scenario to validate old localStorage tokens
 */
const getTokenExpirationTime = token => {
    if (!token) {
        return null;
    }

    try {
        const parsedToken = JSON.parse(atob(token.split('.')[1]));

        return parsedToken.exp * 1000; // Convert to milliseconds
    } catch (error) {
        Sephora.logger.error('[New Auth Flow] Error parsing token:', error);

        return null;
    }
};

/**
 * TEMPORARY: Check if refresh token is still valid
 * This is only needed for migration scenario to validate old localStorage tokens
 */
const isRefreshTokenValid = refreshToken => {
    const expirationTime = getTokenExpirationTime(refreshToken);

    if (!expirationTime) {
        return false;
    }

    const now = Date.now();

    return now < expirationTime;
};

// ============================================================================
// Scenario Detection & Handlers
// ============================================================================
export const determineCurrentScenario = () => {
    const hasReadableCookie = hasValidProfileStatusCookie();
    const profileStatus = Storage.local.getItem(LOCAL_STORAGE.PROFILE_SECURITY_STATUS);

    Sephora.logger.verbose('[New Auth Flow] Scenario detection (initial):', {
        hasNewCookie: hasReadableCookie,
        profileStatus
    });

    // Scenario 4: Within One Hour (tokens fresh, already migrated)
    if (hasReadableCookie && profileStatus !== null) {
        return AUTH_SCENARIOS.WITHIN_ONE_HOUR;
    }

    // Scenario 1: Anonymous Flow
    if (profileStatus === PROFILE_STATUS.ANONYMOUS || profileStatus === null) {
        return AUTH_SCENARIOS.ANONYMOUS_FLOW;
    }

    // At this point: profileStatus is 2/3/4 but no new cookies
    // This could be either:
    // - Migration scenario (old flow user with valid tokens)
    // - Scenario 6 (old flow user with expired tokens)

    // TEMPORARY: Check old tokens for migration scenario
    // TODO: Remove this logic once all users migrate to new cookie-based flow
    const accessToken = Storage.local.getItem(LOCAL_STORAGE.ACCESS_TOKEN);
    const refreshToken = Storage.local.getItem(LOCAL_STORAGE.REFRESH_TOKEN);

    if (!accessToken || !refreshToken) {
        Sephora.logger.verbose('[New Auth Flow] No old tokens found, treating as anonymous');

        return AUTH_SCENARIOS.ANONYMOUS_FLOW;
    }

    // Validate refresh token expiration
    const isTokenValid = isRefreshTokenValid(refreshToken);

    if (!isTokenValid) {
        // Scenario 6: Refresh token expired
        Sephora.logger.verbose('[New Auth Flow] Scenario 6: Refresh token expired, going anonymous');

        return AUTH_SCENARIOS.ANONYMOUS_FLOW;
    }

    // Migration Scenario: Valid tokens, migrate to new flow
    Sephora.logger.verbose('[New Auth Flow] MIGRATION SCENARIO: Valid refresh token found, migrating to new flow');

    // Scenario 6: After 150 days TTL - because isSSI cookie is missing/expired which means RT is expired
    // At this point they go to anonymous flow
    if (hasValidSSICookie() === false) {
        return AUTH_SCENARIOS.ANONYMOUS_FLOW;
    }

    // Scenario 5
    return AUTH_SCENARIOS.AFTER_ONE_HOUR_BEFORE_150_DAYS;
};

// Scenario 1: Anonymous Flow
const handleAnonymousFlow = async () => {
    Sephora.logger.verbose('[New Auth Flow] Scenario 1: Anonymous');

    // Proactive refresh based on environment threshold:
    // - Production: 30 minutes before expiry
    // - Lower environments (QA): 2.5 minutes before expiry
    if (isTokenExpiringWithinThreshold()) {
        return await initRefreshToken({ causedBy: 'anonymous_within_one_hour_threshold_refresh' });
    }

    return initRefreshToken({ causedBy: 'anonymous_flow' });
};

// Scenario 5: After One Hour, Before 150 Days (Tokens Stale)
const handleAfterOneHour = async () => {
    Sephora.logger.verbose('[New Auth Flow] Scenario 5: Cookie expired');

    return initRefreshToken({ causedBy: 'after_one_hour' });
};

export const executeAuthFlow = async () => {
    const scenario = determineCurrentScenario();

    // Early return if login/generateToken operation in progress
    if (isAuthOperationInProgress()) {
        Sephora.logger.verbose('[New Auth Flow] Auth operation in progress, skipping executeAuthFlow');

        return {
            scenario,
            needsRefresh: false,
            message: 'Auth operation in progress'
        };
    }

    // Early return if tokens are fresh (Scenario 4)
    if (scenario === AUTH_SCENARIOS.WITHIN_ONE_HOUR) {
        Sephora.logger.verbose('[New Auth Flow] Scenario 4: Tokens fresh, skipping');

        // Proactive refresh based on environment threshold:
        // - Production: 30 minutes before expiry
        // - Lower environments (QA): 2.5 minutes before expiry
        if (isTokenExpiringWithinThreshold()) {
            return await initRefreshToken({ causedBy: 'logged_in_within_one_hour_threshold_refresh' });
        }

        return {
            scenario,
            needsRefresh: false,
            message: 'Tokens fresh'
        };
    }

    // Early return if refresh already in progress (prevents duplicate calls)
    if (isRefreshInProgress()) {
        Sephora.logger.verbose('[New Auth Flow] Refresh already in progress, skipping executeAuthFlow');

        return {
            scenario,
            needsRefresh: true,
            message: 'Refresh in progress'
        };
    }

    Sephora.logger.verbose('[New Auth Flow] Executing', { scenario });

    let result;

    switch (scenario) {
        case AUTH_SCENARIOS.ANONYMOUS_FLOW:
            result = await handleAnonymousFlow();

            break;
        case AUTH_SCENARIOS.AFTER_ONE_HOUR_BEFORE_150_DAYS:
            result = await handleAfterOneHour();

            break;
        default:
            Sephora.logger.warn('[New Auth Flow] Unknown scenario', { scenario });
            result = { scenario, needsRefresh: false };
    }

    return { scenario, ...result };
};

// ============================================================================
// Utilities
// ============================================================================
export const isUserLoggedIn = () => userUtils.isSignedIn();

export const isUserAnonymous = () => userUtils.isAnonymous();

// ============================================================================
// Response Interception (Scenario 3: Proactive Refresh at 30 min)
// Scenario 3 is detected via www-authenticate header in API responses
// Scenario 3 is event driven not state driven like 1, 4, and 5
// ============================================================================
export const handleResponse = async response => {
    if (!response?.headers) {
        return;
    }

    const wwwAuth = response.headers.get(WWW_AUTHENTICATE.HEADER_NAME);

    // Parse error type from www-authenticate header
    const errorType = wwwAuthParser.parseWwwAuthenticateHeader(wwwAuth)?.error;

    if (!errorType) {
        return;
    }

    const {
        NO_TOKEN, INVALID_TOKEN, EXPIRED_TOKEN, NEEDS_AUTHENTICATION, TOKEN_EXCHANGE
    } = wwwAuthParser.WWW_AUTHENTICATE_ERRORS;

    // Token-related errors that should trigger refresh.
    // TOKEN_EXCHANGE is a proactive refresh signal returned on 200 responses at the half-at-exp mark.
    const REFRESHABLE_ERRORS = new Set([NO_TOKEN, INVALID_TOKEN, EXPIRED_TOKEN, TOKEN_EXCHANGE]);

    if (REFRESHABLE_ERRORS.has(errorType)) {
        // Handle token-related errors: trigger refresh
        Sephora.logger.verbose(`[New Auth Flow] Detected www-authenticate error="${errorType}", refreshing`);

        initRefreshToken({ causedBy: 'proactive_refresh_30min' })
            .then(() => Sephora.logger.verbose('[New Auth Flow] Proactive refresh complete'))
            .catch(error => Sephora.logger.error('[New Auth Flow] Proactive refresh failed', error));

        return;
    }

    if (errorType === NEEDS_AUTHENTICATION) {
        // Handle needs_authentication: prompt user to sign in, sign out if they cancel
        Sephora.logger.verbose('[New Auth Flow] Detected needs_authentication, prompting re-authentication');

        // Only dispatch in browser context (SSR safety)
        if (typeof window === 'undefined') {
            return;
        }

        // ResetUser event shows login modal first, only signs out if user cancels
        window.dispatchEvent(new CustomEvent('ResetUser'));
    }
};

export const getStatus = () => ({
    scenario: determineCurrentScenario(),
    hasReadableCookie: hasValidProfileStatusCookie(),
    isLoggedIn: isUserLoggedIn(),
    isAnonymous: isUserAnonymous(),
    newAuthFlowRefreshInProgress: isRefreshInProgress()
});

export default {
    hasValidProfileStatusCookie,
    isTokenExpiringWithinThreshold,
    initRefreshToken,
    isRefreshInProgress,
    determineCurrentScenario,
    executeAuthFlow,
    isUserLoggedIn,
    isUserAnonymous,
    handleResponse,
    getStatus,
    setAuthOperationInProgress,
    isAuthOperationInProgress
};
